import { useStore } from '@store/index';
import React, { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { isExternalAppPath } from '@/utils/postLoginNavigation';
import { sanitizeRedirectUrl } from '@/utils/urlValidation';

const fullPageLoad = (url: string) => window.location.replace(url);

/** Public routes (login): authenticated users are sent on to their redirect target. */
export const PublicRoute = ({
  children,
  replace = fullPageLoad,
}: {
  children: ReactNode;
  replace?: (url: string) => void;
}) => {
  const { isAuthenticated } = useStore();
  const location = useLocation();

  // Same source and sanitizer as useAuth's completeLogin.
  const params = new URLSearchParams(location.search);
  const target = sanitizeRedirectUrl(params.get('redirect') || location.state?.from?.pathname);
  const leaving = isAuthenticated && location.pathname === '/login' && isExternalAppPath(target);

  // completeLogin normally owns this load. A logged-in direct visit (Back, a
  // stale tab, a bookmark) has no completeLogin, so do it here; the same URL
  // twice is harmless.
  useEffect(() => {
    if (leaving) replace(target);
  }, [leaving, target, replace]);

  if (isAuthenticated && location.pathname === '/login') {
    // Another app serves this path. completeLogin owns that full page load;
    // a router <Navigate> here would race it and the catch-all would send the
    // user to "/".
    if (leaving) return null;

    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};
